import { useTranslation } from 'react-i18next';
import type { ConsoleActionView, CuratedPrompt } from '../../../services/console-ai-actions.service';

interface Props {
  action: ConsoleActionView;
  isFavorite: boolean;
  onSelect: (promptKey: string, slots: string[] | undefined) => void;
  onToggleFavorite: () => void;
}

export function TemplateCard({ action, isFavorite, onSelect, onToggleFavorite }: Props) {
  const { t } = useTranslation('console-ai');

  const firstPrompt: CuratedPrompt | undefined = action.curatedPrompts[0];
  const slotCount = firstPrompt?.fillableSlots?.length ?? 0;

  const handleClick = () => {
    if (!firstPrompt) return;
    onSelect(firstPrompt.i18nKey, firstPrompt.fillableSlots);
  };

  return (
    <div
      className="border border-white/10 rounded-lg p-3 bg-white/5 hover:bg-white/10 cursor-pointer flex flex-col gap-2 transition-colors"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-white leading-snug">
          {t(action.i18nKey)}
        </span>
        <button
          className="shrink-0 text-sm leading-none opacity-70 hover:opacity-100 transition-opacity"
          title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          {isFavorite ? '★' : '☆'}
        </button>
      </div>

      <code className="text-xs text-sky-400 font-mono truncate">{action.id}</code>

      <div className="flex items-center gap-1.5 flex-wrap">
        {slotCount > 0 && (
          <span className="text-xs bg-white/10 rounded px-1.5 py-0.5 text-slate-300">
            {slotCount} slot{slotCount !== 1 ? 's' : ''}
          </span>
        )}
        {action.mutating && !action.destructive && (
          <span className="text-xs bg-amber-500/20 text-amber-300 rounded px-1.5 py-0.5">mutating</span>
        )}
        {action.destructive && (
          <span className="text-xs bg-red-500/20 text-red-300 rounded px-1.5 py-0.5">destructive</span>
        )}
      </div>
    </div>
  );
}
